import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Breadcrumbs } from "../../../components/Breadcrumbs";
import type { BreadcrumbItem } from "../../../components/Breadcrumbs";
import { PageShell } from "../../shared/layouts/PageShell";
import { PageHeader } from "../../shared/ui/layouts/PageHeader";
import { ConfirmDialog } from "../../../components/ConfirmDialog";
import { useAuth } from "../../../providers/AuthProvider";
import { ApiError, getValidationErrors } from "../../../services/apiClient";
import {
  archiveWorkspace,
  getWorkspace,
  restoreWorkspace,
  updateWorkspace,
  type UpdateWorkspacePayload,
  type Workspace,
} from "../api/workspaces";

import { ArrowLeft } from "@phosphor-icons/react";
import { defaultIconProps } from "../../shared/ui/icons";
import { FormFieldLabel } from "../../../components/FormFieldLabel";
import { useToast } from "../../../providers/ToastProvider";
import { SurfaceCard } from "../../shared/ui/surface/SurfaceCard";

const pageStackClasses = "flex flex-col gap-8 text-slate-900 dark:text-slate-100";
const layoutGridClasses =
  "grid gap-6 items-start lg:grid-cols-[minmax(0,1.1fr)_minmax(320px,1fr)] xl:gap-8";
const formStackClasses = "flex flex-col gap-6";
const fieldClasses = "flex flex-col gap-2";
const inputClasses =
  "block w-full rounded-xl border-0 py-2.5 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-sky-600 sm:text-sm sm:leading-6 disabled:bg-slate-50 disabled:text-slate-500 disabled:ring-slate-200 dark:bg-slate-900 dark:text-slate-100 dark:ring-slate-700 dark:placeholder-slate-500 dark:disabled:bg-slate-800";
const ghostButtonClasses = "pm-btn pm-btn-ghost";
const primaryButtonClasses = "pm-btn pm-btn-primary";
const dangerButtonClasses = "pm-btn pm-btn-danger";

const formatFieldName = (field: string): string => {
  const lastSegment = field.split(".").pop() ?? field;
  return lastSegment
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export function WorkspaceEditPage() {
  const { t } = useTranslation();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const toast = useToast();

  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);

  const workspaceLotsPath = workspaceId ? `/workspaces/${workspaceId}/lots` : "/workspaces";
  const workspaceLinkState = useMemo(
    () => (workspace ? { workspace } : undefined),
    [workspace],
  );
  const redirectState = useMemo(
    () => (workspaceLinkState ? { ...workspaceLinkState, refreshed: true } : { refreshed: true }),
    [workspaceLinkState],
  );

  useEffect(() => {
    let isActive = true;
    const loadWorkspace = async () => {
      if (!accessToken || !workspaceId) {
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const result = await getWorkspace(accessToken, workspaceId);
        if (!isActive) {
          return;
        }
        if (result.role !== "owner") {
          setError(t("workspaces.edit.permission_error"));
          setWorkspace(null);
          setIsLoading(false);
          return;
        }
        setWorkspace(result);
        setName(result.name);
        setDescription(result.description ?? "");
      } catch (err) {
        if (!isActive) {
          return;
        }
        const message =
          err instanceof ApiError
            ? err.message
            : t("workspaces.edit.load_error");
        setError(message);
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    void loadWorkspace();

    return () => {
      isActive = false;
    };
  }, [accessToken, workspaceId, t]);

  const breadcrumbItems = useMemo<BreadcrumbItem[]>(() => {
    return [
      { label: t("workspaces.my_workspaces.title"), to: "/workspaces" },
      {
        label: workspace?.name ?? t("navigation.to_workspace"),
        to: workspaceLotsPath,
        state: workspaceLinkState,
      },
      { label: t("actions.edit") },
    ];
  }, [workspace?.name, workspaceLotsPath, workspaceLinkState, t]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!accessToken || !workspaceId || !workspace) {
      return;
    }
    if (!name.trim()) {
      setFieldErrors({ name: t("workspaces.edit.name_required") });
      setError(t("workspaces.edit.name_required"));
      return;
    }
    setIsSaving(true);
    setError(null);
    setFieldErrors({});
    try {
      const payload: UpdateWorkspacePayload = {
        name: name.trim(),
      };
      const trimmedDescription = description.trim();
      if (description.length === 0 || trimmedDescription.length === 0) {
        payload.description = "";
      } else {
        payload.description = trimmedDescription;
      }

      const result = await updateWorkspace(accessToken, workspaceId, payload);
      setWorkspace(result);
      setName(result.name);
      setDescription(result.description ?? "");
      toast.success(t("workspaces.edit.update_success"));
    } catch (err) {
      if (err instanceof ApiError) {
        setFieldErrors(getValidationErrors(err));
        const message = err.body?.message ?? t("workspaces.edit.update_error");
        setError(message);
      } else {
        setFieldErrors({});
        setError(t("workspaces.edit.update_error"));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(workspaceLotsPath, { state: redirectState });
  };

  const handleArchiveWorkspace = async () => {
    if (!accessToken || !workspaceId || !workspace) {
      return;
    }
    setIsArchiving(true);
    setError(null);
    setFieldErrors({});
    try {
      const updated = await archiveWorkspace(accessToken, workspaceId);
      setWorkspace(updated);
      setName(updated.name);
      setDescription(updated.description ?? "");
      toast.success(t("workspaces.edit.archive_success"));
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : t("workspaces.edit.archive_error");
      setFieldErrors(err instanceof ApiError ? getValidationErrors(err) : {});
      setError(message);
    } finally {
      setIsArchiving(false);
    }
  };

  const handleRestoreWorkspace = async () => {
    if (!accessToken || !workspaceId || !workspace) {
      return;
    }

    setIsRestoring(true);
    setError(null);
    setFieldErrors({});
    try {
      const updated = await restoreWorkspace(accessToken, workspaceId);
      setWorkspace(updated);
      setName(updated.name);
      setDescription(updated.description ?? "");
      toast.success(t("workspaces.edit.restore_success"));
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : t("workspaces.edit.restore_error");
      setFieldErrors(err instanceof ApiError ? getValidationErrors(err) : {});
      setError(message);
    } finally {
      setIsRestoring(false);
    }
  };

  const isArchived = Boolean(workspace?.archived_at);

  const renderFieldErrors = () => {
    if (Object.keys(fieldErrors).length === 0) {
      return null;
    }
    return (
      <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/50 dark:bg-rose-900/20 dark:text-rose-300" role="alert">
        <ul>
          {Object.entries(fieldErrors).map(([field, message]) => (
            <li key={field}>
              <strong>{formatFieldName(field)}</strong>: {message}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  if (isLoading) {
    return (
      <PageShell className={pageStackClasses} width="wide">
        <Breadcrumbs items={breadcrumbItems} />
        <PageHeader
          title={t("workspaces.edit.title")}
          subtitle={t("workspaces.edit.active_subtitle")}
        />
        <p className="px-6 py-8 text-center text-slate-500 dark:text-slate-400">{t("workspaces.edit.loading")}</p>
      </PageShell>
    );
  }

  if (!accessToken || !workspaceId || !workspace) {
    return (
      <PageShell className={pageStackClasses} width="wide">
        <Breadcrumbs items={breadcrumbItems} />
        <PageHeader
          title={t("workspaces.edit.title")}
          subtitle={t("workspaces.edit.active_subtitle")}
          description={error ?? t("workspaces.edit.not_found")}
          actions={
            <div className="flex items-center gap-3">
              <button type="button" className={ghostButtonClasses} onClick={handleCancel}>
                <ArrowLeft {...defaultIconProps} aria-hidden />
                {t("actions.back")}
              </button>
            </div>
          }
        />
      </PageShell>
    );
  }

  return (
    <PageShell className={pageStackClasses} width="wide">
      <Breadcrumbs items={breadcrumbItems} />
      <PageHeader
        title={t("workspaces.edit.title")}
        subtitle={
          isArchived
            ? t("workspaces.edit.archived_subtitle")
            : t("workspaces.edit.active_subtitle")
        }
        badge={
          isArchived ? (
            <span className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-rose-700 dark:border-rose-900/50 dark:bg-rose-900/20 dark:text-rose-300">
              {t("workspaces.edit.archived_badge")}
            </span>
          ) : null
        }
        actions={
          <div className="flex items-center gap-3">
            <Link
              to={workspaceLotsPath}
              state={redirectState}
              className={ghostButtonClasses}
            >
              <ArrowLeft {...defaultIconProps} aria-hidden />
              {t("actions.back")}
            </Link>
          </div>
        }
      />

      {error && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/50 dark:bg-rose-900/20 dark:text-rose-300" role="alert">
          {error}
        </div>
      )}
      {renderFieldErrors()}
      <div className={layoutGridClasses}>
        <SurfaceCard as="section">
          <form className={formStackClasses} onSubmit={handleSubmit}>
            <div className={fieldClasses}>
              <label className="block" htmlFor="workspace-name">
                <FormFieldLabel label={t("workspaces.edit.name_label")} />
              </label>
              <input
                id="workspace-name"
                type="text"
                maxLength={255}
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
                disabled={isSaving}
                className={inputClasses}
              />
              <span className="text-xs text-slate-500 dark:text-slate-400">
                {t("workspaces.edit.name_hint")}
              </span>
            </div>

            <div className={fieldClasses}>
              <label className="block" htmlFor="workspace-description">
                <FormFieldLabel label={t("workspaces.edit.description_label")} optional />
              </label>
              <textarea
                id="workspace-description"
                maxLength={1000}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                disabled={isSaving}
                rows={3}
                className={inputClasses}
              />
              <div className="text-right text-xs text-slate-500 dark:text-slate-400">
                {t("workspaces.edit.description_counter", {
                  count: description.trim().length,
                  limit: 1000,
                })}
              </div>
            </div>

            <SurfaceCard.Footer justify="end" gap="base" divider>
              <button
                type="button"
                className={ghostButtonClasses}
                onClick={handleCancel}
                disabled={isSaving}
              >
                {t("actions.cancel")}
              </button>
              <button
                type="submit"
                className={primaryButtonClasses}
                disabled={isSaving || !name.trim()}
              >
                {isSaving ? t("actions.saving") : t("actions.save_changes")}
              </button>
            </SurfaceCard.Footer>
          </form>
        </SurfaceCard>

        <SurfaceCard
          as="section"
          className="border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-100"
        >
          <SurfaceCard.Body gap="base" textTone="inherit">
            <h2 className="text-lg font-semibold">
              {isArchived ? t("workspaces.edit.archived_title") : t("workspaces.edit.archive_title")}
            </h2>
            <p className="text-sm text-rose-700 dark:text-rose-200">
              {isArchived
                ? t("workspaces.edit.archived_description")
                : t("workspaces.edit.archive_description")}
            </p>
          </SurfaceCard.Body>
          <SurfaceCard.Footer justify="end" gap="base" divider>
            {isArchived ? (
              <button
                type="button"
                className={primaryButtonClasses}
                onClick={handleRestoreWorkspace}
                disabled={isRestoring || isArchiving}
              >
                {isRestoring ? t("workspaces.edit.restoring") : t("workspaces.edit.restore_button")}
              </button>
            ) : (
              <button
                type="button"
                className={dangerButtonClasses}
                onClick={() => setShowArchiveConfirm(true)}
                disabled={isArchiving || isRestoring}
              >
                {isArchiving ? t("workspaces.edit.archiving") : t("workspaces.edit.archive_button")}
              </button>
            )}
          </SurfaceCard.Footer>
        </SurfaceCard>
      </div>

      <ConfirmDialog
        open={showArchiveConfirm}
        title={t("workspaces.edit.archive_confirm", { name: workspace.name })}
        confirmLabel={t("workspaces.edit.archive_button")}
        confirmLoading={isArchiving}
        confirmTone="danger"
        cancelLabel={t("actions.cancel")}
        onCancel={() => setShowArchiveConfirm(false)}
        onConfirm={async () => {
          await handleArchiveWorkspace();
          setShowArchiveConfirm(false);
        }}
      />
    </PageShell>
  );
}
